import { CalendarToday } from "@mui/icons-material";
import {
  FormControl,
  FormHelperText,
  IconButton,
  Popover,
} from "@mui/material";
import {
  DateTimeValidationError,
  StaticDateTimePicker,
} from "@mui/x-date-pickers";
import { DateTimeField } from "@mui/x-date-pickers/DateTimeField";
import { FieldChangeHandler } from "@mui/x-date-pickers/internals";
import dayjs from "dayjs";
import { FC, FocusEventHandler, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import useEvent from "react-use-event-hook";
import styled from "styled-components";

import { useDynamicFormsTranslate, useRtl } from "@ryde/redux";
import {
  getDefaultDatetimeUTCValue,
  getRegisterAttributes,
  getUTCDateOrNowInISO,
  ICustomField,
  IStyledAttributes,
  isValidDateTime,
  useFormats,
  useTimezone,
  ValidationHelpers,
} from "@ryde/shared";

import { LocalizationProvider } from "../../common";
import { useInfluencedInput } from "./hooks/use-influenced-input";

const DateTimeEditableStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "date-time-editable-styled",
})`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface IDateTimeTemplate extends ICustomField {
  onBlur?: (value: TDateISO) => void;
  defaultValue?: TDateISO;
  fullWidth?: boolean;
  isPreventFormUpdateOnChange?: boolean;
}

export const DateTimeTemplate: FC<IDateTimeTemplate> = ({
  id,
  name,
  formLabel,
  formAttributes,
  formValidation,
  onBlur,
  e2eData = "data-time-template",
  defaultValue,
  fullWidth,
  formFieldTypeId,
  isPreventFormUpdateOnChange,
}) => {
  const { control, getValues, setValue } = useFormContext();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });
  const { disabled } = getRegisterAttributes(formAttributes);
  const { timezone } = useTimezone();

  const defaultValueComputed = defaultValue
    ? dayjs.utc(defaultValue)
    : getDefaultDatetimeUTCValue(formAttributes?.defaultTime, timezone);
  const { dateTimeFormat, isAMPM } = useFormats();
  const { isRtl } = useRtl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;
  const handleClose = useEvent((): void => {
    setAnchorEl(null);
  });
  const handleClick = useEvent(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setAnchorEl(event.currentTarget);
    },
  );

  // A conditionally disabled field can't evaluate its condition against real values at
  // mount (the dto may not have arrived), so it must not self-default either.
  const hasDisableCondition = Boolean(formAttributes?.disableCondition);

  useEffect(() => {
    // A disabled datetime (audit stamps like createdAt) must not self-default to "now":
    // marking the form dirty here blocks the edit form's dto reset, leaving every field empty.
    if (disabled || hasDisableCondition) {
      return;
    }

    const initialValue = getValues(name);

    if (!initialValue) {
      const isoValue = getUTCDateOrNowInISO(defaultValueComputed, timezone);
      setValue(name, isoValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    defaultValueComputed,
    disabled,
    getValues,
    hasDisableCondition,
    name,
    setValue,
    timezone,
  ]);

  const handleChange: FieldChangeHandler<
    dayjs.Dayjs | null,
    DateTimeValidationError
  > = useEvent(newValueUTCDayJs => {
    if (!isValidDateTime(newValueUTCDayJs)) {
      return;
    }

    const isoValue = getUTCDateOrNowInISO(newValueUTCDayJs, timezone);

    if (!isPreventFormUpdateOnChange) {
      setValue(name, isoValue, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  });
  const setFormUnderlyingValue = useEvent(
    async (newValueUTCDayJs: dayjs.Dayjs | null): Promise<void> => {
      const isoDate = getUTCDateOrNowInISO(newValueUTCDayJs, timezone);

      if (!isPreventFormUpdateOnChange) {
        setValue(name, isoDate, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      setAnchorEl(null);
      onBlur?.(isoDate);
    },
  );

  const handleBlur: FocusEventHandler<HTMLInputElement> = _ => {
    // using value from ref because MUI keeps only string representation of value in event.target.value
    if (onBlur) {
      onBlur?.(getValues(name));
    }
  };

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  // Disabled empty fields display blank instead of a misleading "now" default.
  const emptyDisplayValue = disabled ? null : defaultValueComputed;

  useInfluencedInput({ formAttributes, name, formFieldTypeId });

  return (
    <LocalizationProvider>
      <Controller
        control={control}
        name={name}
        render={({
          field: { onChange, value, ...restField },
          fieldState: { error },
        }) => (
          <FormControl
            data-e2e-template="date-time-template"
            data-e2e-field-name={name}
            required={requiredComputed}
            error={!!error}
          >
            <DateTimeEditableStyled>
              <DateTimeField
                dir="ltr"
                {...restField}
                data-e2e={e2eData}
                data-e2e-id={`${name}_${id || ""}`}
                label={formLabelTranslated}
                required={requiredComputed}
                onChange={handleChange}
                onBlur={handleBlur}
                value={value ? dayjs.utc(value) : emptyDisplayValue}
                format={dateTimeFormat}
                disabled={disabled}
                timezone={timezone}
                slotProps={{
                  textField: {
                    fullWidth,
                    error: !!error,
                    slotProps: {
                      htmlInput: {
                        style: {
                          textAlign: isRtl ? "right" : "left",
                        },
                      },
                    },
                  },
                }}
              />
              {!disabled && (
                <>
                  <IconButton onClick={handleClick}>
                    <CalendarToday />
                  </IconButton>
                  <Popover
                    id={popoverId}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: isRtl ? "left" : "right",
                    }}
                  >
                    <StaticDateTimePicker
                      value={value ? dayjs.utc(value) : defaultValueComputed}
                      timezone={timezone}
                      onChange={handleChange}
                      onAccept={setFormUnderlyingValue}
                      onClose={handleClose}
                      ampm={isAMPM}
                    />
                  </Popover>
                </>
              )}
            </DateTimeEditableStyled>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />
    </LocalizationProvider>
  );
};
